import { Card, CardHeader } from '../../../components/Card.jsx'
import {
  Tile,
  RankTable,
  BarList,
  PrevalenceList,
  SectionIntro,
  MethodNote,
  fmtNum,
} from '../ui.jsx'

/**
 * National Overview — high-level snapshot of the whole ICER Health
 * ecosystem: population metrics across every participating user, two
 * at-a-glance "top 5" summary cards (diseases + chronic conditions) so
 * executives see the headline picture without leaving the landing tab,
 * and a contribution breakdown by organisation category.
 *
 * All data here comes straight from the National Intelligence payload —
 * no extra API calls. The Top-5 cards reuse the same `disease_burden`
 * and `chronic_conditions` sections the dedicated tabs render in full.
 */
export default function Overview({ data, windowDays }) {
  const ov = data.overview || {}
  const breakdown = ov.organisation_breakdown || []

  // Top 5 diseases — from the windowed disease-burden ranking.
  const topDiseases = (data.disease_burden?.top_diagnoses || [])
    .slice(0, 5)
    .map((d) => ({ label: `${d.code} · ${d.description}`, count: d.count }))

  // Top 5 chronic conditions — lifetime prevalence, most-prevalent first,
  // excluding any below the k=5 privacy floor (count null / suppressed).
  const topChronic = (data.chronic_conditions?.conditions || [])
    .filter((c) => !c.suppressed && c.count != null)
    .slice(0, 5)
  const chronicDenom = data.chronic_conditions?.denominator_patients || 0

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Ecosystem snapshot"
          subtitle="Cumulative health footprint across every participating organisation, individual subscriber and member of the general public."
        />
        <SectionIntro>
          ICER Health currently serves <b>{fmtNum(ov.total_patients)}</b> registered
          patients across <b>{fmtNum(ov.total_organizations)}</b> active organisations,
          with <b>{fmtNum(ov.total_encounters)}</b> clinical encounters and{' '}
          <b>{fmtNum(ov.total_diagnoses)}</b> coded diagnoses recorded to date.
        </SectionIntro>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
          <Tile label="Registered patients" value={fmtNum(ov.total_patients)} colour="wantok" />
          <Tile
            label={`Active patients · ${windowDays}d`}
            value={fmtNum(ov.active_patients)}
            sublabel="seen in window"
            colour="emerald"
          />
          <Tile label="Organisations" value={fmtNum(ov.total_organizations)} colour="sky" />
          <Tile label="Total encounters" value={fmtNum(ov.total_encounters)} colour="slate" />
          <Tile
            label={`Encounters · ${windowDays}d`}
            value={fmtNum(ov.encounters_window)}
            colour="indigo"
          />
          <Tile label="Diagnoses recorded" value={fmtNum(ov.total_diagnoses)} colour="amber" />
          <Tile
            label="Chronic conditions recorded"
            value={fmtNum(ov.total_chronic_recorded)}
            colour="rose"
          />
          <Tile label="Screenings performed" value={fmtNum(ov.total_screenings)} colour="violet" />
        </div>
        <MethodNote>
          Aggregates every patient, encounter, diagnosis and screening across all
          participating organisations and individuals. "Active patients" means seen
          in the selected window; the other tiles are lifetime totals.
        </MethodNote>
      </Card>

      {/* ---- At-a-glance Top 5 cards ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader
            title="Top 5 diseases"
            subtitle={`Most frequently coded diagnoses · last ${windowDays} days. See Disease Burden for the full ranking.`}
            right={
              <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-coral-50 text-coral-700 border-coral-200">
                burden
              </span>
            }
          />
          <BarList items={topDiseases} colour="coral" emptyLabel="No diagnoses yet meet the k=5 privacy threshold." />
        </Card>

        <Card>
          <CardHeader
            title="Top 5 chronic conditions"
            subtitle="Highest lifetime prevalence across all patients. See Chronic Conditions for the full list."
            right={
              <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-rose-50 text-rose-700 border-rose-200">
                prevalence
              </span>
            }
          />
          <PrevalenceList
            items={topChronic}
            colour="rose"
            emptyLabel="No chronic conditions yet meet the k=5 privacy threshold."
            denominatorLabel={
              chronicDenom
                ? `Percentages are of ${fmtNum(chronicDenom)} registered patients.`
                : null
            }
          />
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Reach by organisation category"
          subtitle="National utilisation split across the participating categories. Individual subscribers and the general public are pooled into one line."
        />
        <RankTable
          rows={breakdown}
          columns={[
            { key: 'label', label: 'Category', cellClass: 'font-semibold text-slate-900' },
            { key: 'patients', label: 'Patients', align: 'right', render: (r) => fmtNum(r.patients) },
            { key: 'encounters', label: 'Encounters', align: 'right', render: (r) => fmtNum(r.encounters) },
          ]}
        />
      </Card>
    </div>
  )
}
